// Built-in stamp icons for the pass builder (slice 4a-3).
//
// Each icon ships as a single React component that renders either the
// "filled" (solid) or "empty" (outline) state from one `state` prop, driven
// by `currentColor` — so the PassPreview can recolor a whole stamp grid by
// setting `color` on a parent. Bundled here rather than as static .svg files
// so they participate in the JS bundle + tree-shaking and stay theme-aware.
//
// Storage convention: pass_design.stamp_filled_url / stamp_empty_url hold
// `builtin:<id>:filled` / `builtin:<id>:empty` for these, or an uploaded
// image URL for a custom icon. `resolveStampIcon` below turns either form
// into something renderable.

import type { BuiltinStampIcon, StampIconState } from '@naya/shared'
import {
  BUILTIN_STAMP_ICON_GEOMETRY,
  BUILTIN_STAMP_ICONS,
  parseBuiltinRef,
} from '@naya/shared'

export type { StampIconState }

interface StampIconProps {
  state: StampIconState
  className?: string
  /** px size of the square viewport. Defaults to 100% of the container. */
  size?: number
}

// Single React renderer for all built-in icons — drives wrapper attrs off
// state identically to the API-side buildBuiltinStampSvg in @naya/shared,
// so the same icon geometry composites the same way in the wallet hero PNG.
function BuiltinIcon({
  id,
  state,
  className,
  size,
}: StampIconProps & { id: BuiltinStampIcon }) {
  const { viewBox, content } = BUILTIN_STAMP_ICON_GEOMETRY[id]
  const solid = state === 'filled'
  return (
    <svg
      viewBox={viewBox}
      width={size ?? '100%'}
      height={size ?? '100%'}
      className={className}
      aria-hidden="true"
      fill={solid ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={solid ? 0 : 1.8}
      strokeLinejoin="round"
      strokeLinecap="round"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  )
}

/** Human labels for the built-in picker tabs. */
export const BUILTIN_STAMP_ICON_LABELS: Record<BuiltinStampIcon, string> = {
  star: 'Star',
  circle: 'Circle',
  square: 'Square',
  heart: 'Heart',
  coffee: 'Coffee cup',
  paw: 'Paw',
}

export { BUILTIN_STAMP_ICONS, parseBuiltinRef }

/**
 * Render a stamp slot from a pass_design URL/ref. A `builtin:` ref renders the
 * bundled SVG (inherits `currentColor`); anything else is treated as an image
 * URL and rendered as an `<img>`. The `state` argument lets a built-in ref be
 * forced to a state even though the ref itself encodes one — the PassPreview
 * keys filled/empty off the slot index, not the stored ref.
 */
export function StampIcon({
  refValue,
  state,
  className,
}: {
  refValue: string
  state: StampIconState
  className?: string
}) {
  const builtin = parseBuiltinRef(refValue)
  if (builtin) {
    return (
      <BuiltinIcon
        id={builtin.id}
        state={state}
        {...(className ? { className } : {})}
      />
    )
  }
  // Custom uploaded icon — an image URL. Custom icons are pre-rendered for
  // their state, so we just show the URL as given.
  return <img src={refValue} alt="" className={className} />
}
